import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import { infrastructureService } from '../services/api';
import { lerSecao } from '../design';

// Rótulos/cores por tipo de evento
const EVENT_META: Record<string, { label: string; emoji: string; tone: 'up' | 'down' | 'plan' | 'skip' }> = {
  heartbeat_ok: { label: 'Heartbeat OK', emoji: '💚', tone: 'up' },
  jwt_refreshed: { label: 'JWT renovado', emoji: '🔄', tone: 'up' },
  warm_recovery: { label: 'Recuperou (warm-jwt)', emoji: '♻️', tone: 'up' },
  force_relogin_ok: { label: 'Relogin OK', emoji: '🔑', tone: 'up' },
  manual_relogin: { label: 'Relogin manual', emoji: '🙋', tone: 'up' },
  heartbeat_fail: { label: 'Queda', emoji: '🔴', tone: 'down' },
  force_relogin_fail: { label: 'Relogin falhou', emoji: '❌', tone: 'down' },
  cooldown_start: { label: 'Cooldown iniciado', emoji: '🧊', tone: 'down' },
  auto_paused: { label: 'Pausa automática', emoji: '⏸️', tone: 'down' },
  skip_disputa: { label: 'Pulado (disputa ativa)', emoji: '⚔️', tone: 'skip' },
  skip_paused: { label: 'Pulado (pausado)', emoji: '⏸️', tone: 'skip' },
  vnc_restart: { label: 'Reinício planejado', emoji: '🔧', tone: 'plan' },
  manual_reset: { label: 'Reset manual', emoji: '🧹', tone: 'plan' },
};
const toneClass: Record<string, string> = {
  up: 'text-emerald-300', down: 'text-rose-300', plan: 'text-sky-300', skip: 'text-slate-400',
};
const toneBar: Record<string, string> = {
  up: 'bg-emerald-500', down: 'bg-rose-500', plan: 'bg-sky-500', skip: 'bg-slate-600',
};
const fmtAgo = (iso: string): string => {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return 'agora';
  if (m < 60) return `${m}min atrás`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h${String(m % 60).padStart(2, '0')} atrás`;
  return `${Math.floor(h / 24)}d atrás`;
};
const fmtDur = (min: number | null): string => {
  if (min == null) return '—';
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h${String(min % 60).padStart(2, '0')}`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};
const fmtClock = (iso: string): string =>
  new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

interface Props {
  showToast?: (message: string, type?: 'success' | 'error') => void;
}

export default function ComprasNetSessionHistory({ showToast }: Props) {
  const [stats, setStats] = useState<any | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [windowH, setWindowH] = useState(24);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [infra, setInfra] = useState<any | null>(null);
  const timerRef = useRef<any>(null);
  // O que NÃO atualizou na última leitura (08/10/2026). Antes, cada parte caía calada em null: na primeira carga a tela
  // dizia "Sem dados de sessão ainda." sem ter lido nada, e numa recarga que falhava os números velhos ficavam como se
  // fossem de agora. E a janela (24h, 72h…) é um recorte: os números de uma janela não aparecem sob outra.
  const [faltou, setFaltou] = useState<{ partes: string[]; motivo: string } | null>(null);
  const janelaNaTelaRef = useRef<number | null>(null);

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    try {
      if (janelaNaTelaRef.current !== windowH) { setStats(null); setHistory([]); }
      const [s, h, st] = await Promise.all([
        lerSecao(infrastructureService.getSessionStats(windowH)),
        lerSecao(infrastructureService.getSessionHistory({ sinceHours: windowH, limit: 300 })),
        lerSecao(infrastructureService.getStatus()),
      ]);
      if (!s.erro) { setStats(s.valor); janelaNaTelaRef.current = windowH; }
      if (!h.erro && (h.valor as any)?.events) setHistory((h.valor as any).events);
      if (!st.erro && st.valor) setInfra(st.valor);
      const partes = [s.erro && 'as estatísticas', h.erro && 'o histórico', st.erro && 'o status da infraestrutura'].filter(Boolean) as string[];
      setFaltou(partes.length ? { partes, motivo: (s.erro || h.erro || st.erro) as string } : null);
    } catch (err: any) {
      showToast?.('Falha ao buscar histórico: ' + (err?.message || 'erro'), 'error');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [windowH, showToast]);

  useEffect(() => {
    fetchData();
    timerRef.current = setInterval(fetchData, 60000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchData]);

  // Série temporal pro gráfico (ordem cronológica). JWT expIn + latência dos heartbeats.
  const chartData = [...history]
    .filter((e) => e.jwtExpInSec != null || e.latencyMs != null)
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    .map((e) => ({
      t: fmtClock(e.createdAt),
      jwt: e.jwtExpInSec ?? null,
      latency: e.latencyMs != null ? Math.round(e.latencyMs / 100) / 10 : null, // s
    }));

  // Barra de status estilo statuspage (cronológico, mais antigo → recente).
  const statusBar = [...history]
    .filter((e) => EVENT_META[e.eventType])
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  const cs = stats?.currentSession;
  const up = stats?.uptimePct;
  const upColor = up == null ? 'text-slate-300' : up >= 99 ? 'text-white' : up >= 90 ? 'text-amber-300' : 'text-rose-300';

  return (
    <div className="p-6 space-y-6 max-w-[1700px]">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold text-slate-100">Sessão ComprasNet</h2>
          <p className="text-slate-400 text-sm mt-1">
            Uptime, renovações de JWT e reinícios planejados vs. quedas reais. Atualiza a cada 60s.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            {[24, 72, 168, 720].map((h) => (
              <button key={h} onClick={() => setWindowH(h)}
                className={`px-2.5 py-1 rounded text-xs font-medium ${windowH === h ? 'bg-central-elevado text-white font-semibold border border-central-borda' : 'bg-central-cartao text-slate-300 hover:bg-central-elevado border border-central-borda'}`}>
                {h === 24 ? '24h' : h === 72 ? '3d' : h === 168 ? '7d' : '30d'}
              </button>
            ))}
          </div>
          <button onClick={fetchData} disabled={refreshing}
            className="px-3 py-1.5 rounded bg-central-cartao border border-central-borda hover:bg-central-elevado text-slate-100 text-sm disabled:opacity-50">
            {refreshing ? '…' : '↻ Atualizar'}
          </button>
        </div>
      </div>

      {/* Alerta do túnel Mac mini (egress gov.br/captcha). O heartbeat normal usa o
          SOCKS do financeiro (9050) e fica verde MESMO com o Mac mini caído — mas o
          RELOGIN (gov.br + captcha) vai pelo Mac mini (9080). Se ele cai, a sessão
          continua "viva" até o JWT/cookie expirar; aí o robô NÃO consegue relogar. */}
      {(() => {
        const eg = infra?.pncp_egress;
        if (!eg) return null;
        const macDown = !!eg.test_error && /ECONNREFUSED|9080|refus/i.test(String(eg.test_error));
        if (!macDown) return null;
        return (
          <div className="bg-rose-900/40 border border-rose-600 rounded-central p-4 flex items-start gap-3">
            <span className="text-rose-300 text-xl leading-none mt-0.5">🔌</span>
            <div className="flex-1">
              <div className="text-rose-200 font-semibold">Túnel do Mac mini CAÍDO — relogin gov.br indisponível</div>
              <div className="text-rose-100/80 text-sm mt-1">
                O egress do Mac mini (Cascavel) está fora (<span className="font-mono text-xs">{String(eg.test_error).slice(0, 60)}</span>).
                A sessão ComprasNet abaixo pode aparecer <strong>viva</strong> (o heartbeat renova o JWT pelo SOCKS do
                financeiro), <strong>mas o robô NÃO consegue relogar</strong> se o cookie/JWT expirar — o login gov.br + captcha
                passa por este túnel. Numa disputa, a sessão morre quando precisar relogar.
              </div>
              <div className="text-rose-100/60 text-xs mt-1.5">
                Ação: verificar a internet/energia do Mac mini em Cascavel e reiniciar o autossh (ou o Mac mini). Eu não acesso (residencial/NAT).
              </div>
            </div>
          </div>
        );
      })()}

      {faltou && !loading && (
        <div role="alert" className="mb-3 px-3 py-2 rounded-lg border border-amber-600/50 bg-amber-500/10 text-xs text-amber-200" data-sessao="nao-atualizou">
          Não consegui atualizar {faltou.partes.join(', ')} ({faltou.motivo}).
          {stats ? ' Os números abaixo são da última leitura que deu certo, nesta mesma janela.' : ''}
        </div>
      )}
      {loading || (refreshing && !stats) ? (
        <div className="text-slate-400 py-10 text-center">Carregando…</div>
      ) : !stats ? (
        <div className="text-slate-400 py-10 text-center">
          {faltou?.partes.includes('as estatísticas') ? 'Não consegui carregar as estatísticas de sessão: isto não quer dizer que não há sessão.' : 'Sem dados de sessão ainda.'}
        </div>
      ) : (
        <>
          {/* Cartões de métrica */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <div className="bg-central-cartao border border-central-borda rounded-central p-4">
              <div className="text-xs text-slate-400">Uptime</div>
              <div className={`text-3xl font-bold ${upColor}`}>{up == null ? '—' : `${up}%`}</div>
              <div className="text-[11px] text-slate-500">{stats.ticksUp}↑ / {stats.ticksDown}↓</div>
            </div>
            <div className="bg-central-cartao border border-central-borda rounded-central p-4">
              <div className="text-xs text-slate-400">Sessão viva há</div>
              <div className={`text-3xl font-bold ${cs?.live ? 'text-white' : 'text-rose-300'}`}>{cs?.live ? fmtDur(cs.durationMin) : 'caída'}</div>
              <div className="text-[11px] text-slate-500">{cs?.live && cs.since ? `desde ${fmtAgo(cs.since)}` : (cs?.lastEventType ? EVENT_META[cs.lastEventType]?.label : '—')}</div>
            </div>
            <div className="bg-central-cartao border border-central-borda rounded-central p-4">
              <div className="text-xs text-slate-400">Quedas</div>
              <div className={`text-3xl font-bold ${stats.drops > 0 ? 'text-rose-300' : 'text-white'}`}>{stats.drops}</div>
              <div className="text-[11px] text-slate-500">MTBF {stats.mtbfMin ? fmtDur(stats.mtbfMin) : '—'}</div>
            </div>
            <div className="bg-central-cartao border border-central-borda rounded-central p-4">
              <div className="text-xs text-slate-400">JWT renovado</div>
              <div className="text-3xl font-bold text-white">{stats.byType?.jwt_refreshed || 0}</div>
              <div className="text-[11px] text-slate-500">atual {stats.lastJwtExpInSec != null ? `${stats.lastJwtExpInSec}s` : '—'}</div>
            </div>
            <div className="bg-central-cartao border border-central-borda rounded-central p-4">
              <div className="text-xs text-slate-400">Latência média</div>
              <div className="text-3xl font-bold text-slate-200">{stats.avgLatencyMs ? `${(stats.avgLatencyMs / 1000).toFixed(1)}s` : '—'}</div>
              <div className="text-[11px] text-slate-500">p95 {stats.p95LatencyMs ? `${(stats.p95LatencyMs / 1000).toFixed(1)}s` : '—'}</div>
            </div>
            <div className="bg-central-cartao border border-central-borda rounded-central p-4">
              <div className="text-xs text-slate-400">Reinícios planejados</div>
              <div className="text-3xl font-bold text-white">{stats.plannedRestarts?.length || 0}</div>
              <div className="text-[11px] text-slate-500">manutenção</div>
            </div>
          </div>

          {/* Barra de status estilo statuspage */}
          <div className="bg-central-cartao border border-central-borda rounded-central p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-slate-100">Linha do tempo de disponibilidade</h3>
              <div className="flex items-center gap-3 text-[11px] text-slate-400">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> OK</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-rose-500" /> Queda</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-sky-500" /> Manutenção</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-slate-600" /> Pulado</span>
              </div>
            </div>
            {statusBar.length === 0 ? (
              <div className="text-slate-500 text-sm py-4 text-center">Sem eventos na janela.</div>
            ) : (
              <div className="flex items-end gap-[2px] h-12">
                {statusBar.map((e) => {
                  const meta = EVENT_META[e.eventType];
                  return (
                    <div
                      key={e.id}
                      title={`${meta.emoji} ${meta.label} · ${fmtClock(e.createdAt)}${e.reason ? ' · ' + e.reason : ''}`}
                      className={`flex-1 min-w-[2px] rounded-sm ${toneBar[meta.tone]} ${meta.tone === 'down' ? 'h-full' : meta.tone === 'plan' ? 'h-3/4' : meta.tone === 'skip' ? 'h-1/3 opacity-60' : 'h-1/2'} hover:opacity-80 transition-opacity`}
                    />
                  );
                })}
              </div>
            )}
            <div className="flex justify-between text-[10px] text-slate-500 mt-1">
              <span>{statusBar.length ? fmtClock(statusBar[0].createdAt) : ''}</span>
              <span>agora</span>
            </div>
          </div>

          {/* Gráfico JWT + latência */}
          <div className="bg-central-cartao border border-central-borda rounded-central p-5">
            <h3 className="text-base font-semibold text-slate-100 mb-1">JWT (vida restante) × latência do heartbeat</h3>
            <p className="text-slate-400 text-xs mb-3">O JWT vive ~600s e é renovado antes de expirar (dente de serra). A linha vermelha (240s) é o limiar de renovação.</p>
            <div className="h-64">
              {chartData.length < 2 ? (
                <div className="text-slate-500 text-sm py-10 text-center">Poucos pontos pra plotar ainda.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#363d49" />
                    <XAxis dataKey="t" tick={{ fill: '#94a3b8', fontSize: 10 }} minTickGap={40} />
                    <YAxis yAxisId="jwt" tick={{ fill: '#94a3b8', fontSize: 10 }} domain={[0, 650]} label={{ value: 'JWT (s)', angle: -90, position: 'insideLeft', fill: '#7dd3fc', fontSize: 11 }} />
                    <YAxis yAxisId="lat" orientation="right" tick={{ fill: '#94a3b8', fontSize: 10 }} label={{ value: 'Latência (s)', angle: 90, position: 'insideRight', fill: '#cbd5e1', fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ background: '#16181d', border: '1px solid #363d49', borderRadius: 8, fontSize: 12 }}
                      labelStyle={{ color: '#e2e8f0' }}
                      formatter={(v: any, name: string) => name === 'jwt' ? [`${v}s`, 'JWT restante'] : [`${v}s`, 'Latência']}
                    />
                    <ReferenceLine yAxisId="jwt" y={240} stroke="#f87171" strokeDasharray="4 4" />
                    <Area yAxisId="jwt" type="monotone" dataKey="jwt" stroke="#38bdf8" fill="#38bdf8" fillOpacity={0.15} connectNulls dot={false} />
                    <Line yAxisId="lat" type="monotone" dataKey="latency" stroke="#cbd5e1" strokeWidth={1.5} connectNulls dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Recuperações por método + reinícios planejados */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-central-cartao border border-central-borda rounded-central p-5">
              <h3 className="text-base font-semibold text-slate-100 mb-3">Recuperações por método</h3>
              {Object.keys(stats.recoveryByMethod || {}).length === 0 ? (
                <div className="text-slate-500 text-sm">Nenhuma recuperação na janela (sessão estável).</div>
              ) : (
                <div className="space-y-2 text-sm">
                  {Object.entries(stats.recoveryByMethod).map(([m, n]: any) => (
                    <div key={m} className="flex justify-between">
                      <span className="text-slate-400">{m === 'warm_jwt' ? 'Warm-JWT (reload)' : m === 'f5' ? 'F5 (reload)' : m === 'force_relogin' ? 'Force-relogin (captcha)' : m === 'manual' ? 'Manual' : m}</span>
                      <span className="text-slate-200 font-medium">{n}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-central-cartao border border-central-borda rounded-central p-5">
              <h3 className="text-base font-semibold text-slate-100 mb-3">Reinícios planejados (manutenção)</h3>
              {(stats.plannedRestarts || []).length === 0 ? (
                <div className="text-slate-500 text-sm">Nenhum reinício planejado na janela.</div>
              ) : (
                <div className="space-y-2 text-sm max-h-40 overflow-y-auto">
                  {stats.plannedRestarts.map((r: any, i: number) => (
                    <div key={i} className="flex items-start gap-2">
                      <span>🔧</span>
                      <div>
                        <div className="text-slate-200">{r.reason || 'reinício'}</div>
                        <div className="text-[11px] text-slate-500">{fmtClock(r.at)} · {fmtAgo(r.at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Timeline detalhada */}
          <div className="bg-central-cartao border border-central-borda rounded-central p-5">
            <h3 className="text-base font-semibold text-slate-100 mb-3">Eventos recentes</h3>
            <div className="max-h-96 overflow-y-auto">
              {history.length === 0 ? (
                <div className="text-slate-500 text-sm text-center py-4">Sem eventos.</div>
              ) : (
                <div className="space-y-1">
                  {history.map((ev) => {
                    const meta = EVENT_META[ev.eventType] || { label: ev.eventType, emoji: '•', tone: 'skip' as const };
                    return (
                      <div key={ev.id} className="flex items-center gap-2 text-xs py-1.5 border-b border-slate-800/60 last:border-0">
                        <span className="w-5 text-center">{meta.emoji}</span>
                        <span className={`font-medium w-44 ${toneClass[meta.tone]}`}>{meta.label}</span>
                        <span className="text-slate-500 w-24">{fmtAgo(ev.createdAt)}</span>
                        <span className="text-slate-400 flex-1 truncate">
                          {ev.reason ? ev.reason : ev.detail || ''}
                          {ev.jwtExpInSec != null && <span className="text-slate-600 ml-1">· JWT {ev.jwtExpInSec}s</span>}
                          {ev.latencyMs ? <span className="text-slate-600 ml-1">· {(ev.latencyMs / 1000).toFixed(1)}s</span> : null}
                          {ev.activeDisputes ? <span className="text-slate-600 ml-1">· {ev.activeDisputes} disputa(s)</span> : null}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
